import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  FiArrowLeft,
  FiCheck,
  FiChevronRight,
  FiPhone,
  FiPlus,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import { getSession } from "../utils/session";
import { API_URL } from "../services/api";
import "./HabitosPanel.css";

/*
 * Hábitos y motivación.
 *
 * Tres cosas: los hábitos que la persona decide seguir, un check-in de dos
 * barras para ver cómo se encuentra, y una biblioteca de obstáculos reales
 * con qué hacer en cada caso.
 *
 * No es apoyo psicológico y no lo pretende. Si en el check-in aparece algo
 * que se sale de aquí, la pantalla se aparta: deja de enseñar consejos y
 * enseña teléfonos de personas.
 */

const auth = async () => ({ Authorization: `Bearer ${(await getSession()).token}` });

const CARAS = ["😞", "🙁", "😐", "🙂", "😄"];
const ENERGIA = ["Fundido", "Flojo", "Normal", "Bien", "A tope"];

/* ─── Recursos: cuando la pantalla se aparta ─── */

function Recursos({ recursos, onCerrar }) {
  return (
    <section className="hp-recursos" role="alert">
      <h3>{recursos.titulo}</h3>
      <p>{recursos.texto}</p>
      <ul>
        {recursos.contactos.map((c) => (
          <li key={c.telefono}>
            <a href={`tel:${c.telefono}`}>
              <FiPhone aria-hidden="true" />
              <span>
                <strong>{c.nombre}</strong>
                <em>{c.detalle}</em>
              </span>
              <b>{c.telefono}</b>
            </a>
          </li>
        ))}
      </ul>
      <p className="hp-recursos-cierre">{recursos.cierre}</p>
      <button type="button" onClick={onCerrar}>
        Entendido
      </button>
    </section>
  );
}

/* ─── Check-in del día ─── */

function CheckIn({ actual, onGuardar, guardando }) {
  const [animo, setAnimo] = useState(actual?.animo || 0);
  const [energia, setEnergia] = useState(actual?.energia || 0);
  const [nota, setNota] = useState(actual?.nota || "");
  const [abierto, setAbierto] = useState(!actual);

  if (!abierto) {
    return (
      <button type="button" className="hp-checkin-hecho" onClick={() => setAbierto(true)}>
        <span aria-hidden="true">{CARAS[(actual.animo || 3) - 1]}</span>
        <span>Ya has registrado cómo estás hoy</span>
        <FiChevronRight aria-hidden="true" />
      </button>
    );
  }

  return (
    <section className="hp-checkin">
      <h3>¿Cómo estás hoy?</h3>

      <div className="hp-campo">
        <span>Ánimo</span>
        <div className="hp-caras">
          {CARAS.map((c, i) => (
            <button
              key={c}
              type="button"
              className={animo === i + 1 ? "hp-cara hp-cara-on" : "hp-cara"}
              onClick={() => setAnimo(i + 1)}
              aria-label={`Ánimo ${i + 1} de 5`}
              aria-pressed={animo === i + 1}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="hp-campo">
        <span>Energía</span>
        <div className="hp-energia">
          {ENERGIA.map((t, i) => (
            <button
              key={t}
              type="button"
              className={energia === i + 1 ? "hp-nivel hp-nivel-on" : "hp-nivel"}
              onClick={() => setEnergia(i + 1)}
              aria-pressed={energia === i + 1}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="hp-campo">
        <span>¿Algo que contar? (opcional)</span>
        <textarea
          rows={2}
          maxLength={600}
          value={nota}
          placeholder="Lo lee tu entrenador"
          onChange={(e) => setNota(e.target.value)}
        />
      </div>

      <button
        type="button"
        className="hp-btn"
        disabled={!animo || guardando}
        onClick={() => onGuardar({ animo, energia, nota }).then(() => setAbierto(false))}
      >
        {guardando ? "Guardando…" : "Guardar"}
      </button>
    </section>
  );
}

/* ─── Un hábito ─── */

function Habito({ h, onMarcar, onBorrar }) {
  const cumple = h.estaSemana >= h.objetivoSemanal;

  return (
    <article className="hp-habito">
      <button
        type="button"
        className={h.hechoHoy ? "hp-marcar hp-marcar-on" : "hp-marcar"}
        onClick={() => onMarcar(h._id)}
        aria-label={h.hechoHoy ? `Desmarcar ${h.nombre}` : `Marcar ${h.nombre} como hecho hoy`}
        aria-pressed={h.hechoHoy}
      >
        {h.hechoHoy ? <FiCheck /> : <span aria-hidden="true">{h.icono}</span>}
      </button>

      <div className="hp-habito-cuerpo">
        <div className="hp-habito-top">
          <strong>{h.nombre}</strong>
          <button
            type="button"
            className="hp-borrar"
            onClick={() => onBorrar(h._id)}
            aria-label={`Quitar ${h.nombre}`}
          >
            <FiTrash2 />
          </button>
        </div>

        <div className="hp-habito-datos">
          <span className={cumple ? "hp-ok" : ""}>
            {h.estaSemana}/{h.objetivoSemanal} esta semana
          </span>
          {h.racha > 1 && <span className="hp-racha">🔥 {h.racha} días seguidos</span>}
        </div>

        {/* Las últimas cuatro semanas, un cuadradito por día */}
        <div className="hp-rejilla" aria-hidden="true">
          {h.ultimos.map((d) => (
            <i key={d.dia} className={d.hecho ? "hp-dia hp-dia-ok" : "hp-dia"} title={d.dia} />
          ))}
        </div>
      </div>
    </article>
  );
}

/* ─── Panel ─── */

function HabitosPanel() {
  const [datos, setDatos] = useState(null);
  const [obstaculos, setObstaculos] = useState([]);
  const [abierto, setAbierto] = useState(null);
  const [recursos, setRecursos] = useState(null);
  const [anadiendo, setAnadiendo] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const cargar = useCallback(async () => {
    try {
      const headers = await auth();
      const [h, o] = await Promise.all([
        axios.get(`${API_URL}/habitos`, { headers }),
        axios.get(`${API_URL}/habitos/obstaculos`, { headers }),
      ]);
      setDatos(h.data);
      setObstaculos(o.data);
      setError("");
    } catch {
      setError("No se pudo cargar.");
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const marcar = async (id) => {
    // Optimista: el cuadradito se pinta al instante
    setDatos((d) => ({
      ...d,
      habitos: d.habitos.map((h) =>
        h._id === id
          ? { ...h, hechoHoy: !h.hechoHoy, estaSemana: h.estaSemana + (h.hechoHoy ? -1 : 1) }
          : h
      ),
    }));
    try {
      const res = await axios.post(`${API_URL}/habitos/${id}/marcar`, {}, { headers: await auth() });
      setDatos((d) => ({ ...d, habitos: d.habitos.map((h) => (h._id === id ? res.data : h)) }));
    } catch {
      cargar();
    }
  };

  const anadir = async (sug) => {
    setAnadiendo(false);
    try {
      const res = await axios.post(
        `${API_URL}/habitos`,
        { nombre: sug.nombre, icono: sug.icono, objetivoSemanal: sug.porDefecto },
        { headers: await auth() }
      );
      setDatos((d) => ({ ...d, habitos: [...d.habitos, res.data] }));
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo añadir.");
    }
  };

  const borrar = async (id) => {
    setDatos((d) => ({ ...d, habitos: d.habitos.filter((h) => h._id !== id) }));
    try {
      await axios.delete(`${API_URL}/habitos/${id}`, { headers: await auth() });
    } catch {
      cargar();
    }
  };

  const guardarCheckIn = async (valores) => {
    setGuardando(true);
    try {
      const res = await axios.put(`${API_URL}/habitos/checkin`, valores, { headers: await auth() });
      setDatos((d) => ({ ...d, checkInHoy: res.data.checkIn }));
      // Si la red de seguridad ha saltado, la pantalla se aparta
      if (res.data.recursos) setRecursos(res.data.recursos);
    } catch {
      setError("No se pudo guardar.");
    }
    setGuardando(false);
  };

  if (error && !datos) return <div className="app-estado app-estado-error">{error}</div>;
  if (!datos) return <div className="app-estado">Cargando…</div>;

  // Detalle de un obstáculo
  if (abierto) {
    return (
      <article className="hp-detalle">
        <button type="button" className="hp-volver" onClick={() => setAbierto(null)}>
          <FiArrowLeft /> Volver
        </button>
        <h2>{abierto.titulo}</h2>
        <p className="hp-sintoma">{abierto.sintoma}</p>

        <section className="hp-porque">
          <h3>Por qué pasa</h3>
          <p>{abierto.porQue}</p>
        </section>

        <section className="hp-lista hp-lista-si">
          <h3>Qué puedes probar</h3>
          <ul>
            {abierto.quePuedesHacer.map((t) => (
              <li key={t}>
                <FiCheck aria-hidden="true" /> {t}
              </li>
            ))}
          </ul>
        </section>

        <section className="hp-lista hp-lista-no">
          <h3>Qué no ayuda</h3>
          <ul>
            {abierto.queNoHacer.map((t) => (
              <li key={t}>
                <FiX aria-hidden="true" /> {t}
              </li>
            ))}
          </ul>
        </section>

        <p className="hp-nota">
          Si lo que te pasa no encaja con nada de esto, díselo a tu entrenador
          por el chat. Está para eso.
        </p>
      </article>
    );
  }

  return (
    <div className="hp">
      {recursos && <Recursos recursos={recursos} onCerrar={() => setRecursos(null)} />}

      {/* Mientras haya recursos en pantalla no se enseñan consejos de hábitos */}
      {!recursos && (
        <>
          <CheckIn actual={datos.checkInHoy} onGuardar={guardarCheckIn} guardando={guardando} />

          <section className="hp-bloque">
            <header>
              <h3>Tus hábitos</h3>
              <button type="button" className="hp-anadir" onClick={() => setAnadiendo((v) => !v)}>
                <FiPlus aria-hidden="true" /> Añadir
              </button>
            </header>

            {anadiendo && (
              <div className="hp-sugeridos">
                {datos.sugeridos
                  .filter((s) => !datos.habitos.some((h) => h.nombre === s.nombre))
                  .map((s) => (
                    <button key={s.id} type="button" onClick={() => anadir(s)}>
                      <span aria-hidden="true">{s.icono}</span> {s.nombre}
                      <em>{s.porDefecto}/sem</em>
                    </button>
                  ))}
              </div>
            )}

            {datos.habitos.length === 0 ? (
              <p className="hp-vacio">
                Elige uno o dos para empezar. No más: el truco no es llevar diez
                hábitos, es que los dos que lleves duren tres meses.
              </p>
            ) : (
              <div className="hp-habitos">
                {datos.habitos.map((h) => (
                  <Habito key={h._id} h={h} onMarcar={marcar} onBorrar={borrar} />
                ))}
              </div>
            )}
          </section>

          <section className="hp-bloque">
            <header>
              <h3>¿Qué te está frenando?</h3>
            </header>
            <p className="hp-nota">
              Casi todo el mundo se atasca en uno de estos sitios. No es falta de
              fuerza de voluntad: normalmente es que el plan no estaba hecho para
              una semana normal.
            </p>
            <ul className="hp-obstaculos">
              {obstaculos.map((o) => (
                <li key={o.id}>
                  <button type="button" onClick={() => setAbierto(o)}>
                    <span>
                      <strong>{o.titulo}</strong>
                      <em>{o.sintoma}</em>
                    </span>
                    <FiChevronRight aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}

export default HabitosPanel;
